import { APP_VERSIONS } from 'constants/appVersions';
import { LANGUAGES } from 'constants/languages';
import { Resources } from 'constants/resources';

import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { required, useGetList } from 'react-admin';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import {
  FilterAutocompleteChip,
  FilterEnumAutocompleteChip,
  FilterTextChip,
} from 'shared/mui/Filter/FilterChipsToolbar/FilterChips';
import { AutocompleteInput, EnumAutocompleteInput, TextInput } from 'shared/react-admin/Inputs';
import { ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { Country } from 'types/embedded/country';
import { Currency } from 'types/embedded/currency';

export const OrderMethodsListFilterChips: React.FC = () => {
  const translate = useTypedTranslate();

  const { onReset } = useFilterContext();

  const { data: countries = [] } = useGetList<Country>(Resources.Countries);

  return (
    <>
      <FilterAutocompleteChip
        label={translate('app.filter.merchant')}
        name="merchantId"
        onDelete={onReset}
        resource="merchants"
      >
        <ReferenceInput
          name="merchantId"
          reference="merchants"
          resource="merchants"
          source="merchantId"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label={translate('app.filter.merchant')}
            name="merchantId"
            optionText="name"
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
      </FilterAutocompleteChip>
      <FilterTextChip label={translate('app.orderMethods.domain')} name="domain">
        <TextInput
          helperText={false}
          label={translate('app.orderMethods.domain')}
          name="domain"
          source="domain"
          variant="outlined"
        />
      </FilterTextChip>
      <FilterTextChip label={translate('app.orderMethods.appVersion')} name="appVersion">
        <AutocompleteInput
          choices={APP_VERSIONS}
          helperText={false}
          label={translate('app.orderMethods.appVersion')}
          name="appVersion"
          optionText="id"
          source="appVersion"
          variant="outlined"
        />
      </FilterTextChip>
      <FilterTextChip
        getName={(filterValue: string) =>
          countries.find((v) => filterValue === v.alphaCode)?.name || ''
        }
        label={translate('app.filter.country')}
        name="country"
        onDelete={onReset}
      >
        <AutocompleteInput
          choices={countries}
          helperText={false}
          label={translate('app.filter.country')}
          name="country"
          optionText="name"
          optionValue="alphaCode"
          source="country"
          validate={required()}
          variant="outlined"
        />
      </FilterTextChip>
      <FilterTextChip label={translate('app.filter.currency')} name="currency" onDelete={onReset}>
        <ReferenceInput
          filter={{ active: true }}
          name="currency"
          perPage={300}
          reference="currencies"
          resource="currencies"
          source="currency"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            helperText={false}
            label={translate('app.filter.currency')}
            name="currency"
            optionText={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
            optionValue="alphaCode"
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
      </FilterTextChip>
      <FilterTextChip
        label={translate('app.orderMethods.clientId')}
        name="customerId"
        onDelete={onReset}
      >
        <TextInput
          helperText={false}
          id="customerId"
          label={translate('app.orderMethods.clientId')}
          name="customerId"
          source="customerId"
          validate={required()}
          variant="outlined"
        />
      </FilterTextChip>
      <FilterTextChip
        getName={(filterValue: string) =>
          LANGUAGES?.find((v) => filterValue === v.code)?.title || ''
        }
        label={translate('app.filter.language')}
        name="language"
      >
        <AutocompleteInput
          choices={LANGUAGES}
          helperText={false}
          label={translate('app.filter.language')}
          name="language"
          optionText="title"
          optionValue="code"
          source="language"
          variant="outlined"
        />
      </FilterTextChip>
      <FilterTextChip label={translate('app.orderMethods.depositsNumber')} name="countDeposit">
        <TextInput
          helperText={false}
          label={translate('app.orderMethods.depositsNumber')}
          name="countDeposit"
          source="countDeposit"
          variant="outlined"
        />
      </FilterTextChip>
      <FilterEnumAutocompleteChip
        label={translate('app.filter.direction')}
        name="direction"
        onDelete={onReset}
        resource="directions"
      >
        <EnumAutocompleteInput
          filterToQuery={(searchText: string) => ({ description: searchText })}
          fullWidth
          helperText={false}
          label={translate('app.filter.direction')}
          name="direction"
          optionText="description"
          optionValue="value"
          resource="directions"
          source="direction"
          validate={required()}
          variant="outlined"
        />
      </FilterEnumAutocompleteChip>
    </>
  );
};
